import type { PasteEditorLinkHandlers } from '../modals/paste-editor-link-modal';
import type { AttachUploadLinkHandlers } from '../tools/files/editorjs-attach-upload-link-tool';
import type {
  GalleryEditRequest,
  GalleryImageViewRequest,
} from '../tools/gallery/editorjs-gallery-tool';
import type { Dispatch, RefObject, SetStateAction } from 'react';

import { useEffect } from 'react';

import type { EditorJsData } from '#pages/document/editorjs-types';

import AddToChatTune from '../tunes/editorjs-add-to-chat-tune';
import AiInstructionsTune, {
  type AiInstructionsEditRequest,
} from '../tunes/editorjs-ai-instructions-tune';

import { createSafeStrikethroughTool } from './strikethrough-list-range-trim';

/** Values read at call time by Editor.js tools so the instance is not recreated when props/context change. */
export type EditorDynamicConfig = {
  onUpload?: (file: File) => Promise<string>;
  onAddToChat?: (blockId: string) => void;
  isSourceVisible: (entryId: string) => boolean;
  setSourceVisible: (entryId: string, visible: boolean) => void;
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  onOpenGalleryImage?: (request: GalleryImageViewRequest) => void;
  onRequestAiInstructionsEdit?: (request: AiInstructionsEditRequest) => void;
  onRequestAttachUploadLink?: (handlers: AttachUploadLinkHandlers) => void;
  onRequestPasteEditorLink?: (handlers: PasteEditorLinkHandlers) => void;
};

export type UseEditorSetupParams = {
  holderRef: RefObject<HTMLDivElement | null>;
  editorRef: RefObject<any | null>;
  persistChainRef: RefObject<Promise<unknown>>;
  latestValueRef: RefObject<EditorJsData | null>;
  dynamicConfigRef: RefObject<EditorDynamicConfig>;
  commitEditorOutput: (data: EditorJsData) => void;
  schedulePersist: (run: () => Promise<void>) => Promise<unknown>;
  registerDocumentHeadingPortalTarget: (id: string, el: HTMLElement) => void;
  unregisterDocumentHeadingPortalTarget: (id: string) => void;
  registerFilesPortalTarget: (id: string, el: HTMLElement) => void;
  unregisterFilesPortalTarget: (id: string) => void;
  setDocumentHeadingPortalTargets: Dispatch<
    SetStateAction<Map<string, HTMLElement>>
  >;
  setFilesPortalTargets: Dispatch<SetStateAction<Map<string, HTMLElement>>>;
};

export function useEditorSetup({
  holderRef,
  editorRef,
  persistChainRef,
  latestValueRef,
  dynamicConfigRef,
  commitEditorOutput,
  schedulePersist,
  registerDocumentHeadingPortalTarget,
  unregisterDocumentHeadingPortalTarget,
  registerFilesPortalTarget,
  unregisterFilesPortalTarget,
  setDocumentHeadingPortalTargets,
  setFilesPortalTargets,
}: UseEditorSetupParams): void {
  useEffect(() => {
    let cancelled = false;

    const setup = async () => {
      if (!holderRef.current) return;

      try {
        const [
          { default: EditorJS },
          { default: CodeTool },
          HeaderModule,
          { default: ImageTool },
          { default: EditorjsList },
          { default: TableTool },
          { DocumentHeadingTool },
          { default: MermaidTool },
          { GalleryTool },
          { FilesTool },
          { AttachUploadLinkTool },
          StrikethroughModule,
        ] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/code'),
          import('@editorjs/header'),
          import('@editorjs/image'),
          import('@editorjs/list'),
          import('@editorjs/table'),
          import('../tools/document-heading/editorjs-document-heading-tool'),
          import('../tools/mermaid/editorjs-mermaid-tool'),
          import('../tools/gallery/editorjs-gallery-tool'),
          import('../tools/files/editorjs-files-tool'),
          import('../tools/files/editorjs-attach-upload-link-tool'),
          import('editorjs-strikethrough'),
        ]);

        if (cancelled || !holderRef.current) return;

        const hasUploader = Boolean(dynamicConfigRef.current.onUpload);

        const headerClass =
          typeof HeaderModule === 'function'
            ? HeaderModule
            : ((HeaderModule as any).default ?? HeaderModule);

        const StrikethroughTool =
          (StrikethroughModule as any)?.default ?? StrikethroughModule;

        const SafeStrikethroughTool =
          createSafeStrikethroughTool(StrikethroughTool);

        /** Shared inline tools (link, formatting). Applied via root `inlineToolbar`; blocks that should not allow inline marks set `inlineToolbar: false`. */
        const inlineToolbarTools: string[] = [
          'link',
          'attachUploadLink',
          'bold',
          'italic',
          // Editor.js strikethrough tool (wraps with <del class="cdx-strikethrough">).
          'strikethrough',
        ];

        const editor = new EditorJS({
          holder: holderRef.current,
          data: (latestValueRef.current as any) ?? { blocks: [] },
          autofocus: true,
          placeholder: 'Start writing, or type / to insert blocks…',
          /** Default order for inline tools (paragraph, headers, lists, etc.). */
          inlineToolbar: inlineToolbarTools,
          tools: {
            header: {
              class: headerClass,
              config: {
                levels: [1, 2, 3],
                defaultLevel: 2,
              },
              toolbox: [
                {
                  title: 'Heading 1',
                  icon: 'H1',
                  data: {
                    text: '',
                    level: 1,
                  },
                },
                {
                  title: 'Heading 2',
                  icon: 'H2',
                  data: {
                    text: '',
                    level: 2,
                  },
                },
                {
                  title: 'Heading 3',
                  icon: 'H3',
                  data: {
                    text: '',
                    level: 3,
                  },
                },
              ],
            },
            list: {
              class: EditorjsList,
              toolbox: [
                {
                  title: 'Bulleted list',
                  icon: '•',
                  data: {
                    style: 'unordered',
                    meta: {},
                    items: [
                      {
                        content: '',
                        meta: {},
                        items: [],
                      },
                    ],
                  },
                },
                {
                  title: 'Numbered list',
                  icon: '1.',
                  data: {
                    style: 'ordered',
                    meta: {},
                    items: [
                      {
                        content: '',
                        meta: {},
                        items: [],
                      },
                    ],
                  },
                },
              ],
            },
            table: {
              // @editorjs/table typings require `config` on constructor; Editor.js passes optional config.
              class: TableTool as any,
            },
            strikethrough: {
              class: SafeStrikethroughTool as any,
            },
            code: {
              class: CodeTool,
              inlineToolbar: false,
            },
            image: {
              class: ImageTool,
              inlineToolbar: false,
              toolbox: {
                title: 'Image',
                icon: '🖼',
              },
              ...(hasUploader
                ? {
                    config: {
                      uploader: {
                        async uploadByFile(file: File) {
                          const upload = dynamicConfigRef.current.onUpload;
                          if (!upload) {
                            return { success: 0 };
                          }

                          const url = await upload(file);
                          return {
                            success: 1,
                            file: {
                              url,
                            },
                          };
                        },
                      },
                    },
                  }
                : {}),
            },
            mermaid: {
              class: MermaidTool,
              inlineToolbar: false,
              toolbox: {
                title: 'Diagram (Mermaid)',
                icon: '📊',
              },
              config: {
                getShowSource: (entryId: string) =>
                  dynamicConfigRef.current.isSourceVisible(entryId),
                setShowSource: (entryId: string, visible: boolean) =>
                  dynamicConfigRef.current.setSourceVisible(entryId, visible),
              },
            },
            documentHeading: {
              class: DocumentHeadingTool,
              config: {
                registerPortalTarget: registerDocumentHeadingPortalTarget,
                unregisterPortalTarget: unregisterDocumentHeadingPortalTarget,
              },
            },
            files: {
              class: FilesTool,
              inlineToolbar: false,
              config: {
                registerFilesPortalTarget,
                unregisterFilesPortalTarget,
              },
            },
            attachUploadLink: {
              class: AttachUploadLinkTool as any,
              config: {
                onRequestAttachUploadLink: (
                  handlers: AttachUploadLinkHandlers,
                ) => {
                  dynamicConfigRef.current.onRequestAttachUploadLink?.(
                    handlers,
                  );
                },
              },
            },
            gallery: {
              class: GalleryTool,
              inlineToolbar: false,
              config: {
                onRequestGalleryEdit: (request: GalleryEditRequest) => {
                  dynamicConfigRef.current.onRequestGalleryEdit?.(request);
                },
                onOpenGalleryImage: (request: GalleryImageViewRequest) => {
                  dynamicConfigRef.current.onOpenGalleryImage?.(request);
                },
              },
            },
            addToChatTune: {
              class: AddToChatTune,
              config: {
                onAddToChatRequested: (blockId: string) => {
                  dynamicConfigRef.current.onAddToChat?.(blockId);
                },
              },
            },
            aiInstructions: {
              class: AiInstructionsTune,
              config: {
                onRequestAiInstructionsEdit: (
                  request: AiInstructionsEditRequest,
                ) => {
                  dynamicConfigRef.current.onRequestAiInstructionsEdit?.(
                    request,
                  );
                },
              },
            },
          },
          tunes: ['addToChatTune', 'aiInstructions'],
          onChange(api) {
            void schedulePersist(async () => {
              const data = (await api.saver.save()) as EditorJsData;
              commitEditorOutput(data);
            });
          },
        });

        editorRef.current = editor;
      } catch {
        if (holderRef.current) {
          holderRef.current.innerText =
            'Failed to load editor. Check console for details.';
        }
      }
    };

    void setup();

    return () => {
      cancelled = true;
      persistChainRef.current = Promise.resolve();
      const editor = editorRef.current;
      if (editor) {
        void editor.isReady
          .then(() => {
            editor.destroy();
            editorRef.current = null;
          })
          .catch(() => {
            editorRef.current = null;
          });
      }

      setDocumentHeadingPortalTargets(new Map());
      setFilesPortalTargets(new Map());
    };
  }, [
    commitEditorOutput,
    registerDocumentHeadingPortalTarget,
    registerFilesPortalTarget,
    schedulePersist,
    unregisterDocumentHeadingPortalTarget,
    unregisterFilesPortalTarget,
  ]);
}
